import { useLayoutEffect, useRef } from 'react';

import { useLocation } from 'react-router-dom';

interface IScrollPosition {
  x: number;
  y: number;
}

interface IUseScrollRestorationProps {
  isReady?: boolean;
}

interface IRouterLocation {
  hash: string;
  key?: string;
  pathname: string;
  search: string;
}

const SCROLL_STORAGE_PREFIX = 'skiloope:scroll-position:';
const RESTORE_RETRY_DELAY = 100;
const RESTORE_RETRY_LIMIT = 30;
const RESTORE_POSITION_TOLERANCE = 1;
const RETRY_INCREMENT = 1;

const getStorageKey = (locationKey: string): string =>
  `${SCROLL_STORAGE_PREFIX}${locationKey}`;

const getLocationKey = (location: IRouterLocation): string =>
  location.key || `${location.pathname}${location.search}${location.hash}`;

const getSavedPosition = (storageKey: string): IScrollPosition | null => {
  try {
    const value = window.sessionStorage.getItem(storageKey);

    if (!value) {
      return null;
    }

    const parsedValue = JSON.parse(value) as Partial<IScrollPosition>;

    if (!Number.isFinite(parsedValue.x) || !Number.isFinite(parsedValue.y)) {
      return null;
    }

    return { x: parsedValue.x!, y: parsedValue.y! };
  } catch {
    return null;
  }
};

const savePosition = (storageKey: string): void => {
  try {
    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify({ x: window.scrollX, y: window.scrollY }),
    );
  } catch {
    // Scroll restoration is an enhancement; storage failures must not block routing.
  }
};

const saveScrollPositionForLocation = (location: IRouterLocation): void => {
  savePosition(getStorageKey(getLocationKey(location)));
};

const useScrollRestoration = ({
  isReady = true,
}: IUseScrollRestorationProps = {}): void => {
  const location = useLocation();
  const locationKey = getLocationKey(location);
  const restoredLocationKeyRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (!isReady || restoredLocationKeyRef.current === locationKey) {
      return undefined;
    }

    const savedPosition = getSavedPosition(getStorageKey(locationKey));

    if (!savedPosition) {
      return undefined;
    }

    let retryCount = 0;
    let retryTimeoutId: number | undefined;

    const isRestored = (): boolean =>
      Math.abs(window.scrollX - savedPosition.x) < RESTORE_POSITION_TOLERANCE &&
      Math.abs(window.scrollY - savedPosition.y) < RESTORE_POSITION_TOLERANCE;

    const restorePosition = (): void => {
      window.scrollTo(savedPosition.x, savedPosition.y);

      if (isRestored() || retryCount >= RESTORE_RETRY_LIMIT) {
        restoredLocationKeyRef.current = locationKey;
        return;
      }

      retryCount += RETRY_INCREMENT;
      retryTimeoutId = window.setTimeout(restorePosition, RESTORE_RETRY_DELAY);
    };

    const animationFrameId = window.requestAnimationFrame(restorePosition);

    return () => {
      window.cancelAnimationFrame(animationFrameId);

      if (retryTimeoutId !== undefined) {
        window.clearTimeout(retryTimeoutId);
      }
    };
  }, [isReady, locationKey]);
};

export { saveScrollPositionForLocation, useScrollRestoration };
